import { isSigningComplete } from './utils';
import { SignatureType } from '@dealstack/shared/lib/common/business/contracts';
import { DocumentSigningRoleType, ESignProvider } from '@dealstack/shared/lib/generated';
import type {
	SignableContract,
	WitnessSigningData,
	DocumentWorkflowInstanceSignatureStatus,
} from '@dealstack/shared/lib/generated';

export type SignActionKind =
	| 'SIGN'
	| 'SIGN_WITH_WITNESS'
	| 'RESEND_WITNESS'
	| 'WITNESS_COMPLETE'
	| 'SIGNED'
	| 'NONE';

export interface ResolvedSignAction {
	kind: SignActionKind;
	role: DocumentSigningRoleType;
	requiresWitness: boolean;
	parentSignatureId: string;
	witnessEmail?: string;
}

const isEidEasyContract = (contract: SignableContract): boolean =>
	contract.signatureType === SignatureType.ELECTRONIC &&
	contract.eSignProvider === ESignProvider.EID_EASY;

/**
 * Whether any contract has a representative that requires a witness. Gates the
 * representative witness column, which mirrors the PARTY/PIV witness handling —
 * this must key off the representative's own witness role, not the party's, so a
 * rep-only witness requirement is still surfaced.
 */
export const anyRepresentativeRequiresWitness = (
	contracts: SignableContract[] | undefined
): boolean => !!contracts?.some((contract) => !!contract.representative?.witness?.hasWitnessRole);

const buildSignAction = (args: {
	role: DocumentSigningRoleType;
	isEidEasy: boolean;
	signatureId: string | undefined;
	status: DocumentWorkflowInstanceSignatureStatus | null | undefined;
	witness: WitnessSigningData | undefined;
}): ResolvedSignAction => {
	const { role, isEidEasy, signatureId, status, witness } = args;
	if (!isEidEasy || !signatureId) {
		return { kind: 'NONE', role, requiresWitness: false, parentSignatureId: signatureId ?? '' };
	}
	const requiresWitness = !!witness?.hasWitnessRole;
	const parentSigned = isSigningComplete(status);

	if (requiresWitness) {
		if (!parentSigned) {
			return {
				kind: 'SIGN_WITH_WITNESS',
				role,
				requiresWitness: true,
				parentSignatureId: signatureId,
			};
		}
		const witnessSigned = isSigningComplete(witness?.status);
		return {
			kind: witnessSigned ? 'WITNESS_COMPLETE' : 'RESEND_WITNESS',
			role,
			requiresWitness: true,
			parentSignatureId: signatureId,
			witnessEmail: witness?.email,
		};
	}

	return parentSigned
		? { kind: 'SIGNED', role, requiresWitness: false, parentSignatureId: signatureId }
		: { kind: 'SIGN', role, requiresWitness: false, parentSignatureId: signatureId };
};

export const resolvePartySignAction = (contract: SignableContract): ResolvedSignAction =>
	buildSignAction({
		role: DocumentSigningRoleType.PARTY,
		isEidEasy: isEidEasyContract(contract),
		signatureId: contract.id,
		status: contract.status,
		witness: contract.witness,
	});

export const resolvePivSignAction = (contract: SignableContract): ResolvedSignAction =>
	buildSignAction({
		role: DocumentSigningRoleType.PIV,
		isEidEasy: isEidEasyContract(contract),
		signatureId: contract.piv?.signatureId,
		status: contract.piv?.status,
		witness: contract.piv?.witness,
	});

/**
 * When an investor is also the contract's PIV signer, the PIV signature is
 * forced into the same signing session as the PARTY signature. Returns the
 * PIV action to chain onto PARTY completion, or null if there's nothing
 * pending to chain (no PIV role on this contract, or it's already signed).
 */
export const resolveChainedPivAction = (contract: SignableContract): ResolvedSignAction | null => {
	const pivAction = resolvePivSignAction(contract);
	return pivAction.kind === 'SIGN' || pivAction.kind === 'SIGN_WITH_WITNESS' ? pivAction : null;
};

/**
 * Mirror of resolveChainedPivAction for the reverse direction: when the investor
 * starts from the PIV column, chain onto their still-pending PARTY signature —
 * they're the same signer for both roles, so either entry point must complete both.
 */
export const resolveChainedPartyAction = (
	contract: SignableContract
): ResolvedSignAction | null => {
	const partyAction = resolvePartySignAction(contract);
	return partyAction.kind === 'SIGN' || partyAction.kind === 'SIGN_WITH_WITNESS'
		? partyAction
		: null;
};
